import {inject, Injectable} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {AuthenticationService} from '@modules/authentication';
import {CommunicationCenterService} from '@modules/communication-center';
import {ButtonListDialogComponent, ButtonListDialogData} from 'fuse-core/components/button-list/button-list-dialog/button-list-dialog.component';
import {CardsConfigurationService} from 'fuse-core/components/card/cards-configuration.service';
import {DataCardInterface} from 'fuse-core/components/card/data-card.interface';
import {Usage} from 'fuse-core/components/card/lesson-card-breadcrumb/usage.interface';
import {DataEntity} from 'octopus-connect';
import {Observable, ReplaySubject, Subject} from 'rxjs';
import {filter, mergeMap, take, tap} from 'rxjs/operators';
import {FlagService} from '../../../app/shared/flag.service';
import {Router} from '@angular/router';
import {LRSTracking} from '../../../app/settings';
import {FreemiumPremiumService} from 'shared/fremium-premium/freemium-premium.service';

const COMMUNITY_USAGE_ID = '4055';


@Injectable({
    providedIn: 'root'
})
export class CardsService {

    private cardsConfigurationService = inject(CardsConfigurationService);
    private communicationCenter = inject(CommunicationCenterService);
    private dialog = inject(MatDialog);
    public flagService = inject(FlagService);
    private authService = inject(AuthenticationService);
    private router = inject(Router);
    private freemiumService = inject(FreemiumPremiumService);

    public settingBookmark = new Subject<void>();
    public isAutoAssignmentForced: boolean;
    /** @deprecated le Service est transversal, il peut etre utilisé dans un contexte où il n'y a pas de currentDatacard. Il faut éviter d'utiliser ça et privilégié une datacard passée en parametre de la fonction dont on a besoin */
    public currentDatacard: DataCardInterface<DataEntity>;

    public learners = [];
    public classes = [];
    public workgroups = [];
    private isOneLessonIsLaunchState = false;
    private LRSTracking = LRSTracking;

    public downloadDoc(uri: string): void {
        // send statement to lrs
        if (this.LRSTracking) {
            this.communicationCenter
                .getRoom('lrs')
                .getSubject('media_downloaded')
                .next({
                    id: uri.split('.').pop().split(/\#|\?/)[0],
                    src: uri,
                    type: uri.split('.').pop().split(/\#|\?/)[0]
                });
        }
        // send downloaded custom event to matomo
        if (typeof window !== 'undefined' && window._paq) {
            window._paq.push(['trackLink', uri, 'download']);
        }
        window.open(uri, '_blank');
    }

    public openLessonPage<T extends DataEntity>(dataCard: DataCardInterface<T>, originUrl?: string, queryParams ?: { [key: string]: string | number }): void {
        //ctz-setting
        if (this.cardsConfigurationService.useCurrentRouterUrlFromOriginPath()) {
            this.currentDatacard = dataCard;
            this.currentDatacard.originPath = this.router.url;
            this.communicationCenter.getRoom('lessons').next('openLesson', {
                id: dataCard.resource.id,
                originUrl: this.router.url,
                queryParams: null
            });
        } else {
            this.currentDatacard = dataCard;
            this.communicationCenter.getRoom('lessons').next('openLesson', {
                id: dataCard.resource.id,
                originUrl,
                queryParams: queryParams
            });
        }
    }

    public get displayLessonBreadcrumb(): { usages: boolean, concepts: boolean, chapters: boolean, skills: boolean } {
        return this.cardsConfigurationService.cardDisplay().displayLessonBreadcrumb;
    }

    public get viewLessonPage(): boolean {
        return this.cardsConfigurationService.viewLessonPage();
    }

    private _bookmarksParams: { endPoint: string, type: string } = {endPoint: 'bookmarks', type: 'node'};

    /***
     * params for calling bookmarks
     */
    get bookmarksParams(): { endPoint: string, type: string } {
        return this._bookmarksParams;
    }

    public bookmark(): void {
        this.flagService.flagEntity(this.currentDatacard.resource, this.bookmarksParams.type, this.bookmarksParams.endPoint)
            .subscribe(() => {
                this.settingBookmark.next();
            });
    }

    public playPreview<T extends DataEntity>(datacard?: DataCardInterface<T>) {
        if (datacard) {
            this.currentDatacard = datacard;
        }
        if (this.shouldSelectSubLesson()) {
            const lesson$ = new ReplaySubject<Observable<DataEntity[]>>(1);
            this.communicationCenter
                .getRoom('lessons')
                .next('getLessons', {
                    lessonIds: this.currentDatacard.resource.get('reference').map((subLesson: { id: string | number, title: string }) => subLesson.id),
                    callbackSubject: lesson$
                });
            lesson$.pipe(
                take(1),
                mergeMap(obs => obs),
                mergeMap((subLessonsEntities) => {
                    // data du back intègre pas le isFree mais je l'ai déjà ici
                    const reference = this.currentDatacard.resource.get('reference');
                    const subLessons = subLessonsEntities.map((subLesson, index) => {
                        const referenceSubLesson = reference.find(ref => ref.id.toString() === subLesson.id.toString());
                        let isFree = false;
                        if (referenceSubLesson && referenceSubLesson?.isFree) {
                            isFree = referenceSubLesson?.isFree;
                        }

                        let isEnabledForFreemium = true;
                        if (isFree || !this.freemiumService.isFreemiumPremiumFeatureEnabled()) {
                            isEnabledForFreemium = true;
                        }
                        if (this.freemiumService.isFreemiumPremiumFeatureEnabled() && !isFree && this.freemiumService.isPremiumMode()) {
                            isEnabledForFreemium = true;
                        }
                        if (this.freemiumService.isFreemiumPremiumFeatureEnabled() && !this.freemiumService.isPremiumMode() && !isFree) {
                            isEnabledForFreemium = false;
                        }
                        return {
                            id: subLesson.id.toString(),
                            label: subLesson.get('metadatas').title,
                            value: subLesson.id.toString(),
                            isFree: isFree,
                            isEnabledForFreemium,
                        };
                    });
                    return this.openSubLessonSelectionModal(subLessons);
                }),
                take(1),
                filter(subLessonId => !!subLessonId),
                mergeMap((subLessonId) => this.loadLessonById(subLessonId)),
                tap(subLesson => {
                    const index = this.currentDatacard.resource.get('reference').findIndex((activity) => +activity.id === +subLesson.id);
                    this.currentDatacard.play(this.currentDatacard.resource, <any>{startOnStepIndex: index !== -1 ? index : 0, preview: true});
                })
            ).subscribe();


        } else {
            this.currentDatacard.play(this.currentDatacard.resource, this.cardsConfigurationService.previewOption());
        }
    }

    public shouldSelectSubLesson(): boolean {
        const isNeeded = this.currentDatacard.resource.get('reference') !== undefined
            && this.currentDatacard.resource.get('reference').every((ref: any) => ref.type === 'lesson');
        let isAllowed = false;

        const role = this.authService.accessLevel;
        if (role in this.cardsConfigurationService.chooseSubLessonFeatureActive()) {
            isAllowed = this.cardsConfigurationService.chooseSubLessonFeatureActive()[role];
        } else if ('default' in this.cardsConfigurationService.chooseSubLessonFeatureActive()) {
            isAllowed = this.cardsConfigurationService.chooseSubLessonFeatureActive()['default'];
        }

        return isNeeded && isAllowed;
    }

    public openSubLessonSelectionModal(subLessons): Observable<string | number> {
        return this.dialog.open<ButtonListDialogComponent, ButtonListDialogData>(ButtonListDialogComponent, {
            data: {
                title: this.currentDatacard.resource.get('metadatas').title,
                list: subLessons,
                isFreemiumPremiumFeatureEnabled: this.freemiumService.isFreemiumPremiumFeatureEnabled(),
            }
        }).afterClosed();
    }

    public loadLessonById(subLessonId: string | number): Observable<DataEntity> {
        const lesson$ = new ReplaySubject<Observable<DataEntity>>(1);

        this.communicationCenter
            .getRoom('lessons')
            .next('getLesson', {
                lessonId: subLessonId,
                callbackSubject: lesson$
            });

        return lesson$.pipe(
            mergeMap(obs => obs)
        );
    }

    public defaultUsage(usages: Usage[]): string | null {
        if (!usages || usages.length === 0) {
            return null;
        }

        return (usages.length >= 2 && usages.find((usage) => usage.id === COMMUNITY_USAGE_ID)) ? usages.find((usage) => usage.id === COMMUNITY_USAGE_ID)?.id : usages[0].id;
    }

    /**
     * launch lesson or under menu : settings rules
     * originaly in card component
     * @param dataCard datacard info
     */
    public play<T extends DataEntity>(dataCard: DataCardInterface<T>, options?: { [key: string]: any }): void {
        this.currentDatacard = dataCard;
        if (this.cardsConfigurationService.cardPlayOpenMenu()) {
            // open undermenu before running lesson
        } else {
            // launch lesson
            dataCard.play(dataCard.resource, options);
        }
    }


    public set isOneLessonIsAlreadyLaunch(spinnerState: boolean) {
        this.isOneLessonIsLaunchState = spinnerState;
    }

    public get isOneLessonIsAlreadyLaunch(): boolean {
        return this.isOneLessonIsLaunchState;
    }

    public openAssignmentWithUserDataFormModal(resource: any): void {
        this.communicationCenter.getRoom('activities')
            .next('openAssignmentWithUserDataFormModal', {
                resource
            });
    }
}